import { PropsWithChildren, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { getLegalLinks } from '~/config/legal.config';

import styles from './auth.redesign.module.css';

/* ---------- icons ---------- */
function IconEye({ off }: { off?: boolean }) {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            {off ? (
                <>
                    <path d="M9.88 9.88a3 3 0 0 0 4.24 4.24" />
                    <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
                    <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
                    <path d="m2 2 20 20" />
                </>
            ) : (
                <>
                    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z" />
                    <circle cx="12" cy="12" r="3" />
                </>
            )}
        </svg>
    );
}
function IconArrowLeft() {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
    );
}

/* ---------- shell ---------- */
export function AuthShell({ children }: PropsWithChildren) {
    return (
        <div className={styles.page}>
            <div className={styles.shell}>
                <div className={styles.brand}>
                    <img src="/assets/images/logo-unboared-sm-tr.png" alt="Unboared" />
                    <span className={styles.brandName}>Unboared</span>
                </div>
                <div className={styles.card}>{children}</div>
            </div>
        </div>
    );
}

/* ---------- field ---------- */
export function AuthField({
    id,
    label,
    placeholder,
    password,
    error,
    helper,
    autoComplete,
    onSelect,
    value,
    defaultValue,
    onChange,
    required,
}: {
    id: string;
    label: string;
    placeholder?: string;
    password?: boolean;
    error?: boolean;
    helper?: string;
    autoComplete?: string;
    onSelect?: () => void;
    value?: string;
    defaultValue?: string;
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    required?: boolean;
}) {
    const [shown, setShown] = useState(false);
    let type: 'text' | 'password' = 'text';
    if (password && !shown) type = 'password';

    return (
        <div className={styles.field}>
            <label className={styles.label} htmlFor={id}>
                {label}
            </label>
            <div className={styles.inputWrap}>
                <input
                    id={id}
                    name={id}
                    type={type}
                    placeholder={placeholder}
                    autoComplete={autoComplete}
                    onSelect={onSelect}
                    value={value}
                    defaultValue={defaultValue}
                    onChange={onChange}
                    required={required}
                    aria-invalid={error || undefined}
                    aria-describedby={helper ? `${id}-helper` : undefined}
                    className={`${styles.input} ${password ? styles.hasToggle : ''} ${error ? styles.inputError : ''}`}
                />
                {password && (
                    <button
                        type="button"
                        className={styles.eye}
                        onClick={() => setShown((s) => !s)}
                        aria-label={shown ? 'Hide password' : 'Show password'}
                        tabIndex={-1}
                    >
                        <IconEye off={shown} />
                    </button>
                )}
            </div>
            {helper && (
                <p id={`${id}-helper`} className={styles.helper}>
                    {helper}
                </p>
            )}
        </div>
    );
}

/* ---------- select ---------- */
export function AuthSelect({
    id,
    name,
    label,
    placeholder,
    defaultValue,
    required,
    options,
    onChange,
}: {
    id: string;
    name?: string;
    label: string;
    placeholder?: string;
    defaultValue?: string;
    required?: boolean;
    options: { value: string; label: string }[];
    onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
}) {
    return (
        <div className={styles.field}>
            <label className={styles.label} htmlFor={id}>
                {label}
            </label>
            <select
                id={id}
                name={name ?? id}
                defaultValue={defaultValue ?? ''}
                required={required}
                className={styles.select}
                onChange={onChange}
            >
                {placeholder && (
                    <option value="" disabled>
                        {placeholder}
                    </option>
                )}
                {options.map((o) => (
                    <option key={o.value} value={o.value}>
                        {o.label}
                    </option>
                ))}
            </select>
        </div>
    );
}

/* ---------- buttons ---------- */
function Spinner() {
    return <span className={styles.spinnerDot} aria-label="Loading" />;
}

export function AuthSubmit({ loading, children }: PropsWithChildren<{ loading?: boolean }>) {
    return (
        <button type="submit" className={styles.submit} disabled={loading}>
            {loading ? <Spinner /> : children}
        </button>
    );
}

export function AuthGoogle({ onClick, children }: PropsWithChildren<{ onClick?: () => void }>) {
    return (
        <button type="button" className={styles.google} onClick={onClick}>
            <img src="https://cdn-icons-png.flaticon.com/512/2965/2965278.png" alt="" width={18} height={18} />
            {children}
        </button>
    );
}

export function AuthSeparator({ label }: { label: string }) {
    return (
        <div className={styles.separator}>
            <span>{label}</span>
        </div>
    );
}

export function AuthLink({ onClick, children }: PropsWithChildren<{ onClick?: () => void }>) {
    return (
        <button type="button" className={styles.link} onClick={onClick}>
            {children}
        </button>
    );
}

/**
 * Mention légale (CGU + confidentialité + emails de nouveautés) à côté d'un
 * bouton qui crée un compte. Pas de case à cocher : information seulement.
 * `i18nKey` : texte avec deux balises `<terms>` et `<privacy>` (vérifié par
 * config/legal.config.test.ts dans les 7 langues).
 * Les liens s'ouvrent dans un nouvel onglet pour ne pas perdre le formulaire.
 */
export function AuthLegalNotice({ i18nKey }: { i18nKey: string }) {
    const { i18n } = useTranslation();
    // Langue *résolue* = celle réellement affichée (une langue non supportée
    // retombe sur l'anglais) → le lien suit toujours le texte.
    const links = getLegalLinks(i18n.resolvedLanguage || i18n.language);
    return (
        <p className={styles.legal}>
            <Trans
                i18nKey={i18nKey}
                components={{
                    // Le texte entre les balises est remplacé par la traduction.
                    terms: (
                        <a href={links.terms} target="_blank" rel="noopener noreferrer">
                            terms
                        </a>
                    ),
                    privacy: (
                        <a href={links.privacy} target="_blank" rel="noopener noreferrer">
                            privacy
                        </a>
                    ),
                }}
            />
        </p>
    );
}

export function AuthBack({ onClick, label }: { onClick?: () => void; label: string }) {
    return (
        <button type="button" className={styles.back} onClick={onClick} aria-label={label}>
            <IconArrowLeft />
            {label}
        </button>
    );
}
